import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import {
  PauseCircleOutline as HoldIcon,
  PlayCircleOutline as ReleaseIcon,
  CloseRounded as CloseIcon,
  WarningAmberRounded as WarningIcon,
} from "@mui/icons-material";
import API_URL, {
  equipmentHoldPath,
  equipmentReleaseHoldPath,
} from "../Config";
import axiosInstance from "../axiosinstance.js";
import "./jeconDialog.css";

const HOLD_REASON_MAX_LENGTH = 255;

const apiErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;
  const message = responseData?.message || responseData?.error;
  return typeof message === "string" && message.trim() ? message : fallback;
};

/**
 * Owns the hold/release confirmation flows for one equipment.
 * Authorization remains a backend responsibility; `canManage` only controls
 * whether the frontend offers the actions.
 */
const EquipmentHoldControl = ({
  equipmentId,
  equipmentNumber = "",
  isOnHold = false,
  holdReason = "",
  canManage = false,
  disabled = false,
  compact = false,
  onChanged,
  onRequestError,
}) => {
  const [dialogAction, setDialogAction] = useState(null); // "hold" | "release"
  const [reason, setReason] = useState("");
  const [validationError, setValidationError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isOpen = Boolean(dialogAction);
  const isHoldDialog = dialogAction === "hold";
  const equipmentLabel = equipmentNumber || "this equipment";

  const resetDialog = () => {
    setDialogAction(null);
    setReason("");
    setValidationError("");
    setRequestError("");
  };

  useEffect(() => {
    if (!isOpen) return;

    // If another update changes the equipment state while this confirmation is
    // open, close the now-stale action instead of submitting it.
    if ((dialogAction === "hold" && isOnHold) ||
        (dialogAction === "release" && !isOnHold)) {
      resetDialog();
    }
  }, [dialogAction, isOnHold, isOpen]);

  if (!canManage) return null;

  const openDialog = (action) => {
    if (!equipmentId || disabled || isSubmitting) return;
    setDialogAction(action);
    setReason("");
    setValidationError("");
    setRequestError("");
  };

  const closeDialog = () => {
    if (isSubmitting) return;
    resetDialog();
  };

  const submit = async () => {
    if (!equipmentId || !dialogAction || isSubmitting) return;

    const normalizedReason = reason.trim();
    if (isHoldDialog && !normalizedReason) {
      setValidationError("Please provide a reason for placing the equipment on hold.");
      return;
    }

    setIsSubmitting(true);
    setValidationError("");
    setRequestError("");

    try {
      const path = isHoldDialog
        ? equipmentHoldPath(equipmentId)
        : equipmentReleaseHoldPath(equipmentId);
      const payload = isHoldDialog ? { reason: normalizedReason } : {};
      const response = await axiosInstance.post(`${API_URL}${path}`, payload);
      const nextIsOnHold = isHoldDialog;

      resetDialog();

      if (onChanged) {
        Promise.resolve(
          onChanged({
            action: nextIsOnHold ? "hold" : "release",
            isOnHold: nextIsOnHold,
            response: response?.data,
          }),
        ).catch((callbackError) => onRequestError?.(callbackError));
      }
    } catch (error) {
      const message = apiErrorMessage(
        error,
        isHoldDialog
          ? "Failed to place the equipment on hold. Please try again."
          : "Failed to release the equipment hold. Please try again.",
      );
      setRequestError(message);
      onRequestError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isOnHold ? (
        <Button
          type="button"
          variant={compact ? "outlined" : "contained"}
          startIcon={<ReleaseIcon />}
          onClick={() => openDialog("release")}
          disabled={disabled || isSubmitting || !equipmentId}
          sx={{
            backgroundColor: compact ? "#fff" : "#b71c1c",
            borderColor: "#f1b5ad",
            color: compact ? "#b42318" : "#fff",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            minHeight: compact ? "34px" : undefined,
            padding: compact ? "5px 11px" : undefined,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: compact ? "#fff5f4" : "#991b1b",
              borderColor: "#d92d20",
              boxShadow: "none",
            },
          }}
        >
          Release Hold
        </Button>
      ) : (
        <Button
          type="button"
          variant="outlined"
          startIcon={<HoldIcon />}
          onClick={() => openDialog("hold")}
          disabled={disabled || isSubmitting || !equipmentId}
          sx={{
            borderColor: "#b45309",
            color: "#92400e",
            textTransform: "none",
            fontWeight: 700,
            "&:hover": {
              borderColor: "#92400e",
              backgroundColor: "#fffbeb",
            },
          }}
        >
          Put on Hold
        </Button>
      )}

      <Dialog
        open={isOpen}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: `jecon-dialog equipment-hold-dialog ${isHoldDialog ? "is-hold" : "is-release"}` }}
      >
        <DialogTitle className="equipment-hold-dialog-title">
          <span className="equipment-hold-dialog-title-icon" aria-hidden="true">
            {isHoldDialog ? <HoldIcon /> : <ReleaseIcon />}
          </span>
          <span className="equipment-hold-dialog-title-copy">
            <strong>{isHoldDialog ? "Put Equipment on Hold" : "Release Equipment Hold"}</strong>
            <small>{isHoldDialog ? "Pause all process activity" : "Resume equipment process activity"}</small>
          </span>
          <button
            type="button"
            className="equipment-hold-dialog-close"
            onClick={closeDialog}
            disabled={isSubmitting}
            aria-label="Close dialog"
          >
            <CloseIcon />
          </button>
        </DialogTitle>
        <DialogContent>
          <DialogContentText className="equipment-hold-dialog-prompt">
            {isHoldDialog
              ? `No process activity can be performed on ${equipmentLabel} until its hold is released.`
              : <>Are you sure you want to release the hold on <strong>{equipmentLabel}</strong>?</>}
          </DialogContentText>

          {!isHoldDialog && holdReason && (
            <div className="equipment-hold-dialog-reason">
              <WarningIcon aria-hidden="true" />
              <div>
                <span>Current hold reason</span>
                <p>{holdReason}</p>
              </div>
            </div>
          )}

          {isHoldDialog && (
            <TextField
              autoFocus
              fullWidth
              required
              multiline
              minRows={3}
              label="Hold reason"
              placeholder="Explain why this equipment must be placed on hold"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                if (validationError) setValidationError("");
                if (requestError) setRequestError("");
              }}
              error={Boolean(validationError)}
              helperText={
                validationError || `${reason.length}/${HOLD_REASON_MAX_LENGTH}`
              }
              inputProps={{ maxLength: HOLD_REASON_MAX_LENGTH }}
              disabled={isSubmitting}
            />
          )}

          {requestError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {requestError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={isSubmitting || (isHoldDialog && !reason.trim())}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : isHoldDialog ? (
                <HoldIcon />
              ) : (
                <ReleaseIcon />
              )
            }
            sx={{
              backgroundColor: isHoldDialog ? "#b45309" : "#b71c1c",
              "&:hover": {
                backgroundColor: isHoldDialog ? "#92400e" : "#991b1b",
              },
            }}
          >
            {isSubmitting
              ? "Please wait..."
              : isHoldDialog
                ? "Put on Hold"
                : "Release Hold"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EquipmentHoldControl;
